import { Merge, SimplifyDeep } from 'type-fest'

import { getOnlyDate } from '~/astra'
import { DipsSchemas } from '~/clients/dips-client'
import { DipsScheduledSurgeriesApiVersion } from '~/hooks/useFlag'
import { getTheaterRoom } from '~/utils/dips/surgeryResources'
import { isNotNullish } from '~/utils/guards'
import { assertUnreachable } from '~/utils/utils'

import { ToArray } from './utils'

export type V4_DIPS_Entity = DipsSchemas['ScheduledSurgeryResult']

type V4PassthroughFields = Omit<V4_DIPS_Entity, 'bookingId' | 'patient' | 'resources'>

type UnifiedPatient = Omit<NonNullable<V4_DIPS_Entity['patient']>, 'id' | 'nationalIdentityNumber'> & {
    personProxyGuid?: NonNullable<V4_DIPS_Entity['patient']>['id']
    nationalId?: NonNullable<V4_DIPS_Entity['patient']>['nationalIdentityNumber']
}

type UnifiedFields = {
    id?: V4_DIPS_Entity['bookingId']
    patient?: UnifiedPatient | undefined
    surgeryResources?: V4_DIPS_Entity['resources'] | undefined

    /** Computed field: `YYYY-MM-DD` of `surgery.plannedTheatreInTime ?? surgery.contact?.checkInTime`. */
    scheduled_date?: string | undefined
    /** Computed field: alias of filled shortName of theater-room slot in `surgeryResources`. */
    room_code?: string | undefined
}

export type UnifiedScheduledSurgeryWithOptionalId = Merge<{ entity_type: 'dips_scheduled_surgery' }, V4PassthroughFields & UnifiedFields>
export type UnifiedScheduledSurgery = SimplifyDeep<{ id: string } & Omit<UnifiedScheduledSurgeryWithOptionalId, 'id'>>

export function isUnifiedScheduledSurgeryWithId(entity: UnifiedScheduledSurgeryWithOptionalId): entity is UnifiedScheduledSurgery {
    return typeof entity.id === 'string'
}

// Makes V4 backwards compatible with deprecated versions by renaming properties
// Using `id` rather than `bookingId` is necessary to preserve the type guard on scheduled vs. unscheduled surgeries
// This also avoids renaming fields across the codebase.
function v4ToUnified(entity: V4_DIPS_Entity): UnifiedScheduledSurgeryWithOptionalId {
    const scheduledTime = entity.plannedTheatreInTime ?? entity.contact?.checkInTime

    const newEntity = {
        ...entity,
        entity_type: 'dips_scheduled_surgery',
        id: entity.bookingId,
        patient: {
            ...entity.patient,
            personProxyGuid: entity.patient?.id,
            nationalId: entity.patient?.nationalIdentityNumber,
        },
        surgeryResources: entity.resources,

        // Computed fields
        scheduled_date: scheduledTime ? getOnlyDate(scheduledTime) : undefined,
        room_code: getTheaterRoom(entity.resources)?.shortName ?? undefined,
    } satisfies UnifiedScheduledSurgeryWithOptionalId

    // Remove V4 specific fields that were renamed
    delete newEntity.bookingId
    delete newEntity.patient.id
    delete newEntity.patient.nationalIdentityNumber
    delete newEntity.resources

    return newEntity
}

// This is a map to ensure that if new versions are added to the env, the type system will force us to update the transformer
type IncomingEntity = {
    v4: V4_DIPS_Entity
}[DipsScheduledSurgeriesApiVersion]

function isEntityV4(entity: IncomingEntity): entity is V4_DIPS_Entity {
    return 'bookingId' in entity && 'resources' in entity
}

function transform(entity: IncomingEntity): UnifiedScheduledSurgeryWithOptionalId {
    if (isEntityV4(entity)) return v4ToUnified(entity)

    throw assertUnreachable(entity, {
        bookingId: entity['bookingId'],
        hasResources: isNotNullish(entity['resources']),
    })
}

export function transformScheduledSurgeries(data: ToArray<IncomingEntity>): UnifiedScheduledSurgery[] {
    return data.map(transform).filter(isUnifiedScheduledSurgeryWithId)
}
